import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { Calculator, FileText, Loader2, User, Users } from 'lucide-react'
import { IrsTablesHeader } from './-components/IrsTablesHeader'
import { TaxBracketsTab } from './-components/TaxBracketsTab'
import { StandardDeductionsTab } from './-components/StandardDeductionsTab'
import { UniformLifeTab } from './-components/UniformLifeTab'
import { JointLifeTab } from './-components/JointLifeTab'
import { PageTitle } from '@/components/PageTitle'
import {
  useIrsAvailableYears,
  useIrsTablesData,
  useJointLifeLookup,
} from '@/hooks/repositories/useIrsTablesRepository'
import {
  Tabs,
  TabsContent,
  TabsContents,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'

export const Route = createFileRoute('/_app/irs-tables/')({
  component: IrsTablesPage,
})

function IrsTablesPage() {
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined,
  )
  const [ownerAge, setOwnerAge] = useState<string>('')
  const [spouseAge, setSpouseAge] = useState<string>('')

  const { data: availableYears, isLoading: yearsLoading } =
    useIrsAvailableYears()

  const {
    data: irsData,
    isLoading: dataLoading,
    error,
  } = useIrsTablesData(selectedYear)

  const { data: jointLifeLookupResult } = useJointLifeLookup(
    selectedYear,
    ownerAge,
    spouseAge,
  )

  // Default to most recent year once available years are loaded
  useEffect(() => {
    if (
      availableYears &&
      availableYears.length > 0 &&
      selectedYear === undefined
    ) {
      setSelectedYear(availableYears[0])
    }
  }, [availableYears, selectedYear])

  const jointDisplayData =
    ownerAge || spouseAge
      ? (jointLifeLookupResult ?? [])
      : (irsData?.jointLifeExpectancySample ?? [])

  if (yearsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">
          Loading available years...
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-4 px-6">
        <div className="p-4 border border-destructive/30 bg-destructive/10 rounded-md">
          <p className="text-sm text-destructive">
            Error loading IRS tables data. Please try again.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="py-4 px-6 space-y-4">
      <PageTitle title="IRS Tables" />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <IrsTablesHeader
          selectedYear={selectedYear}
          availableYears={availableYears}
          onYearChange={setSelectedYear}
        />
      </motion.div>

      {/* Main Content */}
      {dataLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="ml-2 text-sm text-muted-foreground">
            Loading IRS tables...
          </span>
        </div>
      ) : irsData ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <Tabs defaultValue="tax-brackets" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger
                value="tax-brackets"
                className="flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <Calculator className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Tax Brackets</span>
              </TabsTrigger>
              <TabsTrigger
                value="deductions"
                className="flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <FileText className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Deductions</span>
              </TabsTrigger>
              <TabsTrigger
                value="uniform-life"
                className="flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <User className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Single Life</span>
              </TabsTrigger>
              <TabsTrigger
                value="joint-life"
                className="flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <Users className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Joint Life</span>
              </TabsTrigger>
            </TabsList>

            <TabsContents>
              <TabsContent value="tax-brackets">
                <TaxBracketsTab
                  year={irsData.year}
                  taxBrackets={irsData.taxBrackets}
                />
              </TabsContent>

              <TabsContent value="deductions">
                <StandardDeductionsTab
                  year={irsData.year}
                  standardDeductions={irsData.standardDeductions}
                />
              </TabsContent>

              <TabsContent value="uniform-life">
                <UniformLifeTab
                  year={irsData.year}
                  entries={irsData.uniformLifeExpectancy}
                />
              </TabsContent>

              <TabsContent value="joint-life">
                <JointLifeTab
                  year={irsData.year}
                  ownerAge={ownerAge}
                  spouseAge={spouseAge}
                  displayData={jointDisplayData}
                  onOwnerAgeChange={setOwnerAge}
                  onSpouseAgeChange={setSpouseAge}
                  onClear={() => {
                    setOwnerAge('')
                    setSpouseAge('')
                  }}
                />
              </TabsContent>
            </TabsContents>
          </Tabs>
        </motion.div>
      ) : null}
    </div>
  )
}
